// A player's card and a team's draft so far, from inside a draft (DRAFT_ROOM.md
// "Board" and "Available players", B-337, B-338): the live room, a mock, and a
// kept mock.
//
// THE DRAFT'S OWN CARD. The league's card is about a season — who holds him,
// this week's points, Add or Claim — and none of it is true in a draft, least
// of all in a mock, where the league's card called a player a free agent a bot
// had just taken. Opened from a draft, the same card says where he went in it,
// or what the board says of him while he is there to take, and offers the
// room's own moves: Draft, and the queue. Everything else on it — his news,
// his weeks ahead, the deeper numbers — is the card as everywhere.
//
// A TEAM'S NAME OPENS ITS DRAFT SO FAR, in a sheet, rather than the league's
// team page: leaving the room mid-draft to see a roster is the one thing a
// drafter cannot afford, and a mock's seat is a bot's, not the league's team.
//
// PRESENTATION ONLY. Every fact is one the draft already sent — the board, the
// available list, the seats — so nothing here is a rule.

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { DraftBoardCell, DraftPlayerRow, DraftSeat } from '@fsp/domain';
import { PositionPill, TeamMark } from '../../kit';
import { Button, Caption, Line, Stack, Text } from '../../kit/ui';
import { PlayerCardsOpenIn } from '../../shell/playerCard';
import { DetailSheet } from '../../templates/DetailSheet';
import { PlayerName } from '../shared/Names';
import { PlayerCard, type DraftCardFacts, type PlayerRef } from '../shared/PlayerCard';
import { pickLabel } from './research';
import { clubLabel } from '../../sports/registry';

/** What the room lets the reader do to a player from his card, when it lets them do anything. */
export interface DraftCardActions {
  /** Whose pick a Draft would make: mine, or the team a commissioner is picking for; null for nobody's. */
  pickingFor: { teamId: string; name: string; mine: boolean } | null;
  pick: (playerId: string, forTeam: string | null) => void;
  /** What pressing it does: a pick, or putting him up in an auction (AUCTION.md). */
  verb?: 'Draft' | 'Nominate';
  queued: (playerId: string) => boolean;
  /** Null for a reader with no seat, who has no queue. */
  toggleQueue: ((playerId: string) => void) | null;
}

/** Trading during the draft, from a team's sheet (DRAFT_ROOM.md, B-346). */
export interface DraftCardTrading {
  /** The reader may offer this team a trade now. */
  canOfferTo: (teamId: string) => boolean;
  propose: (teamId: string) => void;
  /** The draft trades, or has traded: a team's sheet lists its picks to come. */
  on: boolean;
}

const SeatContext = createContext<((teamId: string) => void) | null>(null);

/** A team's name in a draft: it opens that team's draft so far. */
export function SeatName({
  seat,
  className = 'fsp-name-text',
  children,
}: {
  seat: DraftSeat;
  className?: string;
  children?: ReactNode;
}) {
  const open = useContext(SeatContext);
  const words = children ?? seat.team.name;
  if (open === null) return <span className={className}>{words}</span>;
  return (
    <button
      type="button"
      className={`fsp-linklike fsp-name-link ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        open(seat.team.id);
      }}
      title={`${seat.team.name}’s draft so far`}
      aria-label={
        typeof words === 'string' && words !== seat.team.name
          ? `${words}: ${seat.team.name}`
          : seat.team.name
      }
    >
      {words}
    </button>
  );
}

/**
 * A whole element that opens a seat's draft so far: a board's column head,
 * where the name alone is too small a target for a finger.
 */
export function SeatButton({
  seat,
  className,
  children,
  'data-mine': mine,
}: {
  seat: DraftSeat;
  className: string;
  children: ReactNode;
  'data-mine'?: boolean;
}) {
  const open = useContext(SeatContext);
  if (open === null)
    return (
      <div className={className} data-mine={mine}>
        {children}
      </div>
    );
  return (
    <button
      type="button"
      className={className}
      data-mine={mine}
      onClick={() => open(seat.team.id)}
      // ITS OWN WORDS ARE ITS NAME (WCAG 2.5.3): who drafts the seat, as the
      // head shows it; the team and what it opens are the title.
      title={`${seat.team.name}: its draft so far`}
    >
      {children}
    </button>
  );
}

export function DraftCards({
  seats,
  board,
  available,
  actions,
  trading = null,
  kept = false,
  children,
}: {
  seats: readonly DraftSeat[];
  board: readonly DraftBoardCell[];
  available: readonly DraftPlayerRow[];
  /** The room's moves; null where there are none (a kept mock, a finished draft). */
  actions: DraftCardActions | null;
  /** Trading during the draft; null where there is none (a mock, the practice league). */
  trading?: DraftCardTrading | null;
  /** A kept mock: who took a player is said as this mock's. */
  kept?: boolean;
  children: ReactNode;
}) {
  const [player, setPlayer] = useState<PlayerRef | null>(null);
  const [seatId, setSeatId] = useState<string | null>(null);
  const seatOf = useMemo(() => new Map(seats.map((s) => [s.team.id, s])), [seats]);
  const takenAt = useMemo(() => {
    const out = new Map<string, DraftBoardCell>();
    for (const c of board) if (c.player !== null) out.set(c.player.id, c);
    return out;
  }, [board]);
  const rowOf = useMemo(() => new Map(available.map((r) => [r.player.id, r])), [available]);

  const facts = (id: string): DraftCardFacts => {
    const cell = takenAt.get(id) ?? null;
    const row = rowOf.get(id) ?? null;
    const by =
      cell === null
        ? null
        : seatOf.get(cell.teamId)?.team.isMine
          ? 'you'
          : (seatOf.get(cell.teamId)?.team.ownerName ?? 'a team');
    const canPick = cell === null && row !== null && actions?.pickingFor != null;
    return {
      taken:
        cell === null || by === null
          ? null
          : {
              pick: pickLabel(cell.round, cell.slot),
              by,
              kept,
              ...(cell.price === null ? {} : { price: cell.price }),
            },
      row:
        row === null
          ? null
          : {
              rank: row.rank,
              positionRank: row.positionRank,
              tier: row.tier,
              bye: row.bye,
              last: row.last,
            },
      pick:
        canPick && actions?.pickingFor
          ? {
              label: actions.pickingFor.mine
                ? `${actions.verb ?? 'Draft'} ${row.player.shortName}`
                : `${actions.verb ?? 'Draft'} for ${actions.pickingFor.name}`,
              onClick: () => {
                const forTeam = actions.pickingFor?.mine
                  ? null
                  : (actions.pickingFor?.teamId ?? null);
                setPlayer(null);
                actions.pick(id, forTeam);
              },
            }
          : null,
      queue:
        cell === null && row !== null && actions?.toggleQueue
          ? { queued: actions.queued(id), onClick: () => actions.toggleQueue?.(id) }
          : null,
    };
  };

  const seat = seatId === null ? null : (seatOf.get(seatId) ?? null);
  // THE PLAYERS IT HAS NOW, a trade's included, and the picks it has to come.
  const picks =
    seat === null ? [] : board.filter((c) => (c.heldBy ?? c.teamId) === seat.team.id && c.player);
  const toCome =
    seat === null ? [] : board.filter((c) => c.teamId === seat.team.id && c.player === null);
  const nameOf = (teamId: string) => {
    const s = seatOf.get(teamId);
    return s === undefined ? 'a team' : s.team.isMine ? 'you' : s.team.ownerName;
  };
  const counts = new Map<string, number>();
  for (const c of picks) {
    const p = c.player!.positions[0] ?? '';
    counts.set(p, (counts.get(p) ?? 0) + 1);
  }

  return (
    <SeatContext.Provider value={setSeatId}>
      <PlayerCardsOpenIn open={setPlayer}>{children}</PlayerCardsOpenIn>
      <PlayerCard
        data={
          player === null
            ? null
            : {
                // THE SUMMARY THE DRAFT ALREADY HOLDS, so the card shows his
                // position and team before its own read answers, and in the
                // practice league, which has no read.
                player: rowOf.get(player.id)?.player ?? takenAt.get(player.id)?.player ?? player,
                game: null,
                projection: null,
                points: null,
                statLine: null,
                draft: facts(player.id),
              }
        }
        onClose={() => setPlayer(null)}
      />
      <DetailSheet
        opened={seat !== null}
        onClose={() => setSeatId(null)}
        title={seat === null ? undefined : seat.team.name}
      >
        {seat === null ? null : (
          // A NAME IN HERE OPENS HIS CARD IN THIS SHEET'S PLACE, not over it.
          <PlayerCardsOpenIn
            open={(p) => {
              setSeatId(null);
              setPlayer(p);
            }}
          >
            <Stack gap={3}>
              <Line gap={2}>
                <TeamMark team={seat.team} size={28} />
                <div className="fsp-ui-grow">
                  <div className="fsp-name">
                    {seat.team.isMine ? 'Your team' : seat.team.ownerName}
                  </div>
                  <div className="fsp-sub">
                    {seat.ai
                      ? `A bot${seat.ai.difficulty ? ` · ${seat.ai.difficulty}` : ''} · `
                      : ''}
                    Pick {seat.slot} in round one
                  </div>
                </div>
              </Line>
              {/* HOW IT DRAFTS, every phrase (AI_MANAGERS.md §4.7), so a
                  person can see a bot's habits and play around them. */}
              {seat.ai && seat.ai.style.length > 0 ? (
                <div>
                  <Caption>How it drafts</Caption>
                  <Text size="sm" block>
                    {seat.ai.style.join(' · ')}
                  </Text>
                </div>
              ) : null}
              {trading?.canOfferTo(seat.team.id) ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const teamId = seat.team.id;
                    setSeatId(null);
                    trading.propose(teamId);
                  }}
                >
                  Propose a trade
                </Button>
              ) : null}
              {trading?.on && toCome.length > 0 ? (
                <Text size="sm" tone="secondary" block>
                  Picks to come:{' '}
                  {toCome
                    .map(
                      (c) =>
                        `${pickLabel(c.round, c.slot)}${c.originalTeamId === null ? '' : ` (via ${nameOf(c.originalTeamId)})`}`,
                    )
                    .join(', ')}
                </Text>
              ) : null}
              {picks.length === 0 ? (
                <Text tone="secondary" block>
                  No picks yet.
                </Text>
              ) : (
                <>
                  <Text size="sm" tone="secondary" block>
                    {[...counts.entries()]
                      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                      .map(([p, n]) => `${String(n)} ${p}`)
                      .join(' · ')}
                  </Text>
                  <Stack gap={0}>
                    <Caption>Picks</Caption>
                    {picks.map((c) => (
                      <div key={c.overall} className="fsp-row fsp-dr-pickrow">
                        <span className="fsp-num fsp-muted">{pickLabel(c.round, c.slot)}</span>
                        <PositionPill pos={c.player!.positions[0] ?? '—'} size="sm" />
                        <div className="fsp-ui-grow">
                          <div className="fsp-name">
                            <PlayerName player={c.player!} className="fsp-name-text" />
                          </div>
                          <div className="fsp-sub">
                            {c.player!.proTeam === null ? 'FA' : clubLabel(c.player!.proTeam)}
                            {c.heldBy === null ? '' : ` · from ${nameOf(c.teamId)} in a trade`}
                            {c.heldBy === null && c.originalTeamId !== null
                              ? ` · with ${nameOf(c.originalTeamId)}’s pick`
                              : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          </PlayerCardsOpenIn>
        )}
      </DetailSheet>
    </SeatContext.Provider>
  );
}
